import * as React from 'react';
import clsx from 'clsx';
import { IconButton, Theme, createStyles, makeStyles, useTheme } from '@material-ui/core';
import SearchRoundedIcon from '@material-ui/icons/SearchRounded';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';

interface StyleProps {
    accent: string;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        pill: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
            width: '100%',
            height: 40,
            boxSizing: 'border-box',
            borderRadius: 999,
            padding: theme.spacing(0, 1, 0, 1.75),
            backgroundColor: theme.palette.grey[100],
            boxShadow: '0 0 0 0 transparent',
            transition: 'background-color 0.18s ease, box-shadow 0.18s ease',
            '&:hover': {
                backgroundColor: theme.palette.grey[200],
            },
            '@media (prefers-reduced-motion: reduce)': {
                transition: 'none',
            },
        },
        pillFocused: {
            backgroundColor: theme.palette.background.paper,
            // Soft ring in the active tab's own accent (see call sites) rather than a hard
            // border - the fill/ring pair is the field's only focus indicator, doubling as its
            // one deliberate moment of motion.
            boxShadow: ({ accent }: StyleProps) => `0 0 0 3px ${accent}26, 0 1px 4px rgba(0,0,0,0.08)`,
            '&:hover': {
                backgroundColor: theme.palette.background.paper,
            },
        },
        icon: {
            fontSize: 19,
            flexShrink: 0,
            color: theme.palette.text.secondary,
            transition: 'color 0.18s ease',
            '@media (prefers-reduced-motion: reduce)': {
                transition: 'none',
            },
        },
        iconFocused: {
            color: ({ accent }: StyleProps) => accent,
        },
        input: {
            flex: 1,
            minWidth: 0,
            border: 'none',
            outline: 'none',
            background: 'transparent',
            fontFamily: 'inherit',
            fontSize: 14,
            fontWeight: 500,
            color: theme.palette.text.primary,
            padding: 0,
            '&::placeholder': {
                color: theme.palette.text.secondary,
                fontWeight: 400,
                opacity: 1,
            },
        },
        clearButton: {
            padding: 4,
            flexShrink: 0,
            color: theme.palette.text.secondary,
            animation: '$clearIn 0.15s ease',
            '@media (prefers-reduced-motion: reduce)': {
                animation: 'none',
            },
        },
        '@keyframes clearIn': {
            from: { opacity: 0, transform: 'scale(0.6)' },
            to: { opacity: 1, transform: 'scale(1)' },
        },
    })
);

export interface MatterSearchFieldProps {
    hint: string;
    search: string;
    searchAction: (filter: string) => void;
    /** @default true */
    showClear?: boolean;
    /** 6-digit hex color (e.g. '#7E22CE') the focus ring/icon adopt - matches the tab's own
     * accent from MatterDetailsTabList.tsx's TAB_META_BY_ROUTE so the field reads as belonging
     * to that tab rather than a generic shared control. Must be a literal hex, not a var() -
     * the focus ring appends a 2-digit alpha suffix directly onto this value.
     * @default theme.palette.primary.main */
    accent?: string;
    className?: string;
}

// Shared search input for Matter Details tabs with large searchable lists (Documents, Timeline,
// Messages) - explicit request to standardise a field that had three near-identical hand-rolled
// copies (SearchContainer/SearchCol glamorous wrappers around the legacy, Semantic-UI-backed
// RvSearch/RvInput). Scoped to matterDetails rather than replacing RvSearch itself, which is
// still used by ~50 other pages across the app (control panel toolbars) - same "build a scoped
// alternative, don't touch the shared legacy component" approach used for the Parties tab's
// InfoPanel migration.
//
// Built as a plain pill (999px radius, no border at rest) rather than a themed MUI TextField -
// a deliberate break from the 8px-radius rectangular cards used everywhere else in Matter
// Details, so search reads as a distinct, live utility. The only motion is the focus
// transition (fill lifts white, an accent ring appears, the icon crossfades to that accent) -
// nothing else animates.
export const MatterSearchField: React.FC<MatterSearchFieldProps> = (props) => {
    const theme = useTheme<Theme>();
    const accent = props.accent || theme.palette.primary.main;
    const classes = useStyles({ accent });
    const [isFocused, setIsFocused] = React.useState(false);
    const showClear = props.showClear !== false && props.search.length > 0;

    return (
        <div className={clsx(classes.pill, isFocused && classes.pillFocused, props.className)}>
            <SearchRoundedIcon className={clsx(classes.icon, isFocused && classes.iconFocused)} />
            <input
                className={classes.input}
                type="text"
                placeholder={props.hint}
                value={props.search}
                onChange={(event) => props.searchAction(event.target.value)}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                aria-label={props.hint}
            />
            {showClear && (
                <IconButton
                    className={classes.clearButton}
                    size="small"
                    onClick={() => props.searchAction('')}
                    aria-label="Clear search"
                >
                    <CloseRoundedIcon fontSize="small" />
                </IconButton>
            )}
        </div>
    );
};
